import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, ButtonGroup, Card, Container, Form, Modal, Spinner } from 'react-bootstrap';
import { getPlayPage, updatePlayPage } from '../services/api';
import './Play.css';

const slugify = (value) => value
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9\s-]/g, '')
  .replace(/\s+/g, '-')
  .replace(/-+/g, '-');

  const buildTableHtml = (rows, cols, includeHeader) => {
    const safeRows = Math.max(1, Math.min(rows, 12));
    const safeCols = Math.max(2, Math.min(cols, 8));
    const headerCells = Array.from({ length: safeCols })
    .map(() => '<th style="height: 28px;">&nbsp;</th>')
    .join('');
    const rowCount = includeHeader ? Math.max(1, safeRows - 1) : safeRows;
    const bodyRows = Array.from({ length: rowCount })
    .map(() => `<tr>${Array.from({ length: safeCols }).map(() => '<td style="height: 28px;">&nbsp;</td>').join('')}</tr>`)
    .join('');
    const headerHtml = includeHeader ? `<thead><tr>${headerCells}</tr></thead>` : '';
    return `<table>${headerHtml}<tbody>${bodyRows}</tbody></table>`;
  };

function Play() {
  const localDraftKey = 'playEditorDraft';
  const editorRef = useRef(null);
  const [tocItems, setTocItems] = useState([]);
  const [headingLevel, setHeadingLevel] = useState('p');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [, setDraftHtml] = useState('');
  const [showTableModal, setShowTableModal] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const [tableHeader, setTableHeader] = useState(true);
  const [tableContext, setTableContext] = useState(null);
  const savedSelectionRef = useRef(null);
  const resizeStateRef = useRef(null);
  const [textColor, setTextColor] = useState('#111827');
  const textColors = ['#111827', '#6b7280', '#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6'];
  const [showColorPalette, setShowColorPalette] = useState(false);

  const updateToc = useCallback(() => {
    if (!editorRef.current) return;
    const headings = editorRef.current.querySelectorAll('h1, h2, h3, h4');
    const nextItems = [];
    const seenIds = new Map();

    headings.forEach((heading) => {
      const text = heading.textContent?.trim() || '';
      if (!text) return;
      let id = heading.id;
      if (!id) {
        const base = slugify(text);
        const count = seenIds.get(base) || 0;
        id = count ? `${base}-${count}` : base;
        seenIds.set(base, count + 1);
        heading.id = id;
      }
      nextItems.push({
        id,
        text,
        level: Number(heading.tagName.replace('H', ''))
      });
    });

    setTocItems(nextItems);
  }, []);

  const syncDraft = () => {
    if (!editorRef.current) return;
    const content = editorRef.current.innerHTML;
    setDraftHtml(content);
    localStorage.setItem(localDraftKey, content);
  };

  const getSelectedTableCells = useCallback(() => {
    if (!editorRef.current) return [];
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return [];
    const range = selection.getRangeAt(0);
    const anchorNode = selection.anchorNode;
    const focusNode = selection.focusNode;
    const anchorElement = anchorNode?.nodeType === Node.ELEMENT_NODE
      ? anchorNode
      : anchorNode?.parentElement;
    const focusElement = focusNode?.nodeType === Node.ELEMENT_NODE
      ? focusNode
      : focusNode?.parentElement;
    const table = anchorElement?.closest('table') || focusElement?.closest('table');
    if (!table || !editorRef.current.contains(table)) return [];

    const anchorCell = anchorElement?.closest('td, th');
    const focusCell = focusElement?.closest('td, th');
    if (anchorCell && focusCell && anchorCell.closest('table') === table && focusCell.closest('table') === table) {
      const rows = Array.from(table.rows);
      const anchorRowIndex = rows.indexOf(anchorCell.parentElement);
      const focusRowIndex = rows.indexOf(focusCell.parentElement);
      const startRow = Math.min(anchorRowIndex, focusRowIndex);
      const endRow = Math.max(anchorRowIndex, focusRowIndex);
      const startCol = Math.min(anchorCell.cellIndex, focusCell.cellIndex);
      const endCol = Math.max(anchorCell.cellIndex, focusCell.cellIndex);
      const selected = [];
      for (let r = startRow; r <= endRow; r += 1) {
        const row = rows[r];
        if (!row) continue;
        for (let c = startCol; c <= endCol; c += 1) {
          const cell = row.cells[c];
          if (cell) selected.push(cell);
        }
      }
      if (selected.length > 0) return selected;
    }

    const cells = Array.from(table.querySelectorAll('td, th'));
    return cells.filter(cell => range.intersectsNode(cell));
  }, []);

  const applyStyleToCells = (cells, command, value) => {
    cells.forEach((cell) => {
      if (command === 'bold') {
        cell.style.fontWeight = '700';
      } else if (command === 'italic') {
        cell.style.fontStyle = 'italic';
      } else if (command === 'underline' || command === 'strikeThrough') {
        const current = (cell.style.textDecoration || '').split(' ').filter(Boolean);
        const next = new Set(current);
        next.delete('none');
        next.add(command === 'underline' ? 'underline' : 'line-through');
        cell.style.textDecoration = Array.from(next).join(' ');
      } else if (command === 'foreColor') {
        cell.style.color = value;
      }
    });
  };

  const applyCommand = (command, value = null) => {
    const multiCellCommands = new Set(['bold', 'italic', 'underline', 'strikeThrough', 'foreColor']);
    if (multiCellCommands.has(command)) {
      const cells = getSelectedTableCells();
      if (cells.length > 1) {
        applyStyleToCells(cells, command, value);
        updateToc();
        syncDraft();
        return;
      }
    }
    if (editorRef.current) {
      editorRef.current.focus();
    }
    document.execCommand(command, false, value);
    updateToc();
    syncDraft();
  };

  const getResizeTarget = (event) => {
    if (!editorRef.current || !isEditing) return null;
    const cell = event.target.closest('td, th');
    if (!cell || !editorRef.current.contains(cell)) return null;
    const rect = cell.getBoundingClientRect();
    const edgeSize = 6;
    const nearRight = Math.abs(rect.right - event.clientX) <= edgeSize;
    const nearBottom = Math.abs(rect.bottom - event.clientY) <= edgeSize;
    if (!nearRight && !nearBottom) return null;
    return {
      cell,
      row: cell.parentElement,
      colIndex: cell.cellIndex,
      nearRight,
      nearBottom
    };
  };

  const handleEditorMouseMove = (event) => {
    if (!editorRef.current || !isEditing) return;
    if (resizeStateRef.current) return;
    const target = getResizeTarget(event);
    if (target?.nearRight) {
      editorRef.current.style.cursor = 'col-resize';
    } else if (target?.nearBottom) {
      editorRef.current.style.cursor = 'row-resize';
    } else {
      editorRef.current.style.cursor = '';
    }
  };

  const handleEditorMouseDown = (event) => {
    if (!editorRef.current || !isEditing) return;
    const target = getResizeTarget(event);
    if (!target) return;
    event.preventDefault();
    const table = target.cell.closest('table');
    if (!table) return;
    const colCells = Array.from(table.rows).map(row => row.cells[target.colIndex]).filter(Boolean);
    const startX = event.clientX;
    const startY = event.clientY;
    const startWidths = colCells.map(cell => cell.getBoundingClientRect().width);
    const startHeight = target.row?.getBoundingClientRect().height || 0;
    resizeStateRef.current = {
      mode: target.nearRight ? 'col' : 'row',
      colIndex: target.colIndex,
      row: target.row,
      colCells,
      startX,
      startY,
      startWidths,
      startHeight
    };
  };

  useEffect(() => {
    if (!isEditing) return;
    const handleMouseMove = (event) => {
      const state = resizeStateRef.current;
      if (!state) return;
      if (state.mode === 'col') {
        const delta = event.clientX - state.startX;
        state.colCells.forEach((cell, idx) => {
          const nextWidth = Math.max(40, state.startWidths[idx] + delta);
          cell.style.width = `${nextWidth}px`;
        });
      } else if (state.mode === 'row' && state.row) {
        const delta = event.clientY - state.startY;
        const nextHeight = Math.max(24, state.startHeight + delta);
        Array.from(state.row.cells).forEach(cell => {
          cell.style.height = `${nextHeight}px`;
        });
      }
    };
    const handleMouseUp = () => {
      if (!resizeStateRef.current) return;
      resizeStateRef.current = null;
      if (editorRef.current) {
        editorRef.current.style.cursor = '';
      }
      syncDraft();
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isEditing]);

  const handleInsertTable = () => {
    if (editorRef.current) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        if (editorRef.current.contains(range.commonAncestorContainer)) {
          savedSelectionRef.current = range.cloneRange();
        }
      }
    }
    setTableRows(3);
    setTableCols(3);
    setTableHeader(false);
    setShowTableModal(true);
  };

  const handleConfirmInsertTable = () => {
    if (savedSelectionRef.current) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedSelectionRef.current);
      savedSelectionRef.current = null;
    }
    applyCommand('insertHTML', buildTableHtml(tableRows, tableCols, tableHeader));
    setShowTableModal(false);
  };

  const getCellContext = useCallback(() => {
    if (!editorRef.current) return null;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;
    const anchorNode = selection.anchorNode;
    if (!anchorNode) return null;
    const node = anchorNode.nodeType === Node.ELEMENT_NODE
      ? anchorNode
      : anchorNode.parentElement;
    if (!node) return null;
    if (!editorRef.current.contains(node)) return null;
    const cell = node.closest('td, th');
    if (!cell) return null;
    const row = cell.parentElement;
    const table = cell.closest('table');
    if (!row || !table) return null;
    return { cell, row, table, colIndex: cell.cellIndex };
  }, []);

  useEffect(() => {
    if (!isEditing) {
      setTableContext(null);
      return;
    }

    const handleSelection = () => {
      const context = getCellContext();
      setTableContext(context);
    };

    document.addEventListener('selectionchange', handleSelection);
    return () => {
      document.removeEventListener('selectionchange', handleSelection);
    };
  }, [getCellContext, isEditing]);

  const addRow = (position) => {
    if (!tableContext) return;
    const { row, table, colIndex } = tableContext;
    const isHeaderRow = row.parentElement?.tagName.toLowerCase() === 'thead';
    const tbody = table.tBodies[0] || table.appendChild(document.createElement('tbody'));
    const targetRow = isHeaderRow ? null : row;
    const insertIndex = targetRow
      ? (position === 'above' ? targetRow.rowIndex - (table.tHead ? table.tHead.rows.length : 0) : targetRow.rowIndex - (table.tHead ? table.tHead.rows.length : 0) + 1)
      : 0;
    const newRow = tbody.insertRow(insertIndex);
    const cellCount = row.cells.length;
    let focusCell = null;
    for (let i = 0; i < cellCount; i += 1) {
      const cell = newRow.insertCell(i);
      cell.innerHTML = '&nbsp;';
      if (i === Math.min(colIndex, cellCount - 1)) {
        focusCell = cell;
      }
    }
    updateToc();
    syncDraft();
    if (focusCell) {
      const range = document.createRange();
      range.selectNodeContents(focusCell);
      range.collapse(true);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      editorRef.current?.focus();
    }
  };

  const removeRow = () => {
    if (!tableContext) return;
    const { row } = tableContext;
    if (row.parentElement?.tagName.toLowerCase() === 'thead') {
      return;
    }
    const nextRow = row.nextElementSibling || row.previousElementSibling;
    row.remove();
    updateToc();
    syncDraft();
    if (nextRow) {
      const cell = nextRow.cells[0];
      if (cell) {
        const range = document.createRange();
        range.selectNodeContents(cell);
        range.collapse(true);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        editorRef.current?.focus();
      }
    }
  };

  const addColumn = (position) => {
    if (!tableContext) return;
    const { table, colIndex } = tableContext;
    const headRows = table.tHead ? Array.from(table.tHead.rows) : [];
    const bodyRows = Array.from(table.tBodies).flatMap(tbody => Array.from(tbody.rows));
    const insertAt = position === 'left' ? colIndex : colIndex + 1;
    const addCellToRow = (row, tagName) => {
      const cell = document.createElement(tagName);
      cell.innerHTML = '&nbsp;';
      const refCell = row.cells[insertAt] || null;
      if (refCell) {
        row.insertBefore(cell, refCell);
      } else {
        row.appendChild(cell);
      }
    };
    headRows.forEach(row => addCellToRow(row, 'th'));
    bodyRows.forEach(row => addCellToRow(row, 'td'));
    updateToc();
    syncDraft();
    if (bodyRows.length > 0) {
      const targetRow = bodyRows[0];
      const targetCell = targetRow.cells[insertAt];
      if (targetCell) {
        const range = document.createRange();
        range.selectNodeContents(targetCell);
        range.collapse(true);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        editorRef.current?.focus();
      }
    }
  };

  const removeColumn = () => {
    if (!tableContext) return;
    const { table, colIndex } = tableContext;
    const rows = Array.from(table.rows);
    rows.forEach((row) => {
      const cell = row.cells[colIndex];
      if (cell) {
        cell.remove();
      }
    });
    updateToc();
    syncDraft();
    const remainingRows = Array.from(table.rows);
    const focusRow = remainingRows.find(row => row.cells.length > 0);
    if (focusRow) {
      const focusCell = focusRow.cells[Math.min(colIndex, focusRow.cells.length - 1)];
      if (focusCell) {
        const range = document.createRange();
        range.selectNodeContents(focusCell);
        range.collapse(true);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        editorRef.current?.focus();
      }
    }
  };

  const handleHeadingChange = (value) => {
    setHeadingLevel(value);
    applyCommand('formatBlock', value === 'p' ? 'p' : value);
  };

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setLoading(true);
      setSaveError('');
      try {
        const localDraft = localStorage.getItem(localDraftKey);
        const response = await getPlayPage();
        const content = localDraft || response?.data?.content || `
          <h1>Playbook</h1>
          <p>Use this space like a Confluence page. Build outlines, tables, and checklists.</p>
          <h2>Ideas</h2>
          <ul>
            <li>Summarize a setup</li>
            <li>Track signals</li>
          </ul>
          <h2>Checklist</h2>
          <ol>
            <li>Thesis</li>
            <li>Risk</li>
            <li>Trigger</li>
          </ol>
        `;
        if (isMounted) {
          setDraftHtml(content);
          if (editorRef.current) {
            editorRef.current.innerHTML = content;
          }
          updateToc();
        }
      } catch (err) {
        if (isMounted) {
          setSaveError(err?.response?.data?.error || 'Failed to load Play content.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [updateToc]);

  const handleInput = () => {
    if (!editorRef.current || !isEditing) return;
    const content = editorRef.current.innerHTML;
    setDraftHtml(content);
    localStorage.setItem(localDraftKey, content);
    updateToc();
  };

  const handleEditToggle = () => {
    setIsEditing(true);
    setSaveError('');
    setTimeout(() => {
      editorRef.current?.focus();
    }, 0);
  };

  const handleSave = async () => {
    if (!editorRef.current) return;
    const content = editorRef.current.innerHTML;
    setSaving(true);
    setSaveError('');
    try {
      await updatePlayPage(content);
      setDraftHtml(content);
      localStorage.removeItem(localDraftKey);
      setIsEditing(false);
    } catch (err) {
      setSaveError(err?.response?.data?.error || 'Failed to save Play content.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container className="py-4 play-page">
      <div className="play-grid">
        <Card className="play-card">
          <Card.Body>
            {isEditing && (
              <div className="play-toolbar">
                <ButtonGroup size="sm">
                  {[
                    { value: 'p', label: 'P' },
                    { value: 'h1', label: 'H1' },
                    { value: 'h2', label: 'H2' },
                    { value: 'h3', label: 'H3' },
                    { value: 'h4', label: 'H4' }
                  ].map((option) => (
                    <Button
                      key={option.value}
                      variant={headingLevel === option.value ? 'primary' : 'outline-secondary'}
                      onClick={() => handleHeadingChange(option.value)}
                    >
                      {option.label}
                    </Button>
                  ))}
                </ButtonGroup>
                <div className="play-color-control">
                  <button
                    type="button"
                    className="play-color-swatch"
                    style={{ backgroundColor: textColor }}
                    aria-label={`Set text color ${textColor}`}
                    onClick={() => applyCommand('foreColor', textColor)}
                  />
                  <button
                    type="button"
                    className="play-color-toggle"
                    onClick={() => setShowColorPalette(prev => !prev)}
                    aria-label={showColorPalette ? 'Hide color palette' : 'Show color palette'}
                  >
                    {showColorPalette ? '×' : '▼'}
                  </button>
                  {showColorPalette && (
                    <div className="play-color-palette">
                      {textColors.map((color) => (
                        <button
                          key={color}
                          type="button"
                          className="play-color-swatch"
                          style={{ backgroundColor: color }}
                          aria-label={`Set text color ${color}`}
                          onClick={() => {
                            setTextColor(color);
                            applyCommand('foreColor', color);
                            setShowColorPalette(false);
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
                <ButtonGroup size="sm">
                  <Button variant="outline-secondary" onClick={() => applyCommand('bold')}>
                    <strong>B</strong>
                  </Button>
                  <Button variant="outline-secondary" onClick={() => applyCommand('italic')}>
                    <em>I</em>
                  </Button>
                  <Button variant="outline-secondary" onClick={() => applyCommand('underline')}>
                    <span style={{ textDecoration: 'underline' }}>U</span>
                  </Button>
                  <Button variant="outline-secondary" onClick={() => applyCommand('strikeThrough')}>
                    <span style={{ textDecoration: 'line-through' }}>S</span>
                  </Button>
                </ButtonGroup>
                <ButtonGroup size="sm">
                  <Button variant="outline-secondary" onClick={() => applyCommand('insertUnorderedList')}>
                    • List
                  </Button>
                  <Button variant="outline-secondary" onClick={() => applyCommand('insertOrderedList')}>
                    1. List
                  </Button>
                  <Button variant="outline-secondary" onClick={() => applyCommand('formatBlock', 'blockquote')}>
                    Quote
                  </Button>
                </ButtonGroup>
                <ButtonGroup size="sm">
                  <Button variant="outline-secondary" onClick={() => applyCommand('undo')}>Undo</Button>
                  <Button variant="outline-secondary" onClick={() => applyCommand('redo')}>Redo</Button>
                </ButtonGroup>
                <ButtonGroup size="sm">
                  <Button variant="outline-secondary" onClick={() => applyCommand('createLink', prompt('Link URL:') || '')}>
                    Link
                  </Button>
                  <Button variant="outline-secondary" onClick={() => applyCommand('insertHorizontalRule')}>
                    Divider
                  </Button>
                  <Button variant="outline-secondary" onClick={handleInsertTable}>
                    Table
                  </Button>
                </ButtonGroup>
              </div>
            )}
            {isEditing && tableContext && (
              <div className="play-table-toolbar">
                <ButtonGroup size="sm">
                  <Button variant="outline-secondary" onClick={() => addRow('above')}>Row Above</Button>
                  <Button variant="outline-secondary" onClick={() => addRow('below')}>Row Below</Button>
                  <Button variant="outline-secondary" onClick={removeRow}>Delete Row</Button>
                </ButtonGroup>
                <ButtonGroup size="sm">
                  <Button variant="outline-secondary" onClick={() => addColumn('left')}>Col Left</Button>
                  <Button variant="outline-secondary" onClick={() => addColumn('right')}>Col Right</Button>
                  <Button variant="outline-secondary" onClick={removeColumn}>Delete Col</Button>
                </ButtonGroup>
              </div>
            )}
            {saveError && (
              <Alert variant="danger" className="mb-3">
                {saveError}
              </Alert>
            )}
            {loading && (
              <div className="play-loading">
                <Spinner animation="border" size="sm" />
                <span>Loading...</span>
              </div>
            )}
            <div
              className={`play-editor${isEditing ? ' is-editing' : ''}`}
              ref={editorRef}
              contentEditable={isEditing}
              suppressContentEditableWarning
              onInput={handleInput}
              onMouseMove={handleEditorMouseMove}
              onMouseDown={handleEditorMouseDown}
              aria-busy={loading}
            />
          </Card.Body>
        </Card>

        <div className="play-sidebar">
          <div className="play-sidebar-actions">
            {!isEditing ? (
              <Button variant="outline-primary" size="sm" onClick={handleEditToggle}>
                Edit
              </Button>
            ) : (
              <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            )}
          </div>
          <Card className="play-card play-toc">
            <Card.Body>
              <div className="play-toc-title">Table of contents</div>
              {tocItems.length === 0 ? (
                <div className="text-muted small">Add headings to populate the TOC.</div>
              ) : (
                <ul className="play-toc-list">
                  {tocItems.map((item) => (
                    <li key={item.id} className={`play-toc-item level-${item.level}`}>
                      <a href={`#${item.id}`}>{item.text}</a>
                    </li>
                  ))}
                </ul>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>

      <Modal show={showTableModal} onHide={() => setShowTableModal(false)} centered dialogClassName="play-table-modal">
        <Modal.Header closeButton>
          <Modal.Title>Insert Table</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3" controlId="play-table-rows">
              <Form.Label>Rows</Form.Label>
              <Form.Control
                type="number"
                min={2}
                max={12}
                value={tableRows}
                onChange={(event) => setTableRows(Number(event.target.value))}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="play-table-cols">
              <Form.Label>Columns</Form.Label>
              <Form.Control
                type="number"
                min={2}
                max={8}
                value={tableCols}
                onChange={(event) => setTableCols(Number(event.target.value))}
              />
            </Form.Group>
            <Form.Check
              type="switch"
              id="play-table-header"
              label="Include header row"
              checked={tableHeader}
              onChange={(event) => setTableHeader(event.target.checked)}
            />
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowTableModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmInsertTable}>
            Insert
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default Play;
